import { Injectable } from '@angular/core';
import { RequestStatus } from '@models';
import {
  DisputeRequestItemStatus,
  DisputeRequestStatus,
  getDisputeRequestItemStatusName,
  getDisputeRequestStatusName,
} from '@models/dispute-requests.models';
import { getInformationRequestStatusName, InformationRequestStatus } from '@models/information-requests.models';
import {
  getItemClassificationTypeStatusClassName,
  getItemClassificationTypeStatusName,
  ItemClassificationCodeStatus,
  ItemStatus,
} from '@models/items.models';

@Injectable({
  providedIn: 'root',
})
export class ChipHelperService {
  static getItemStatusName(context: StatusContext, status: unknown): string {
    switch (context) {
      case 'item':
        return ItemStatusHelperService.getStatusName(status as ItemStatus);
      case 'item-classification-code':
        return ItemClassificationCodeStatusHelperService.getStatusName(status as ItemClassificationCodeStatus);
      case 'request':
        return RequestStatusHelperService.getStatusName(status as RequestStatus);
      case 'information-request':
        return InformationRequestStatusHelperService.getStatusName(status as InformationRequestStatus);
      case 'dispute-request':
        return DisputeRequestStatusHelperService.getStatusName(status as DisputeRequestStatus);
      case 'dispute-item':
        return DisputeItemStatusHelperService.getStatusName(status as DisputeRequestItemStatus);
    }
  }

  static getItemStatusChipClassName(context: StatusContext, status: unknown): string {
    switch (context) {
      case 'item':
        return ItemStatusHelperService.getClassName(status as ItemStatus);
      case 'item-classification-code':
        return ItemClassificationCodeStatusHelperService.getClassName(status as ItemClassificationCodeStatus);
      case 'request':
        return RequestStatusHelperService.getClassName(status as RequestStatus);
      case 'information-request':
        return InformationRequestStatusHelperService.getClassName(status as InformationRequestStatus);
      case 'dispute-request':
        return DisputeRequestStatusHelperService.getClassName(status as DisputeRequestStatus);
      case 'dispute-item':
        return DisputeItemStatusHelperService.getClassName(status as DisputeRequestItemStatus);
    }
  }
}

export type StatusContext =
  | 'request'
  | 'item'
  | 'information-request'
  | 'item-classification-code'
  | 'dispute-request'
  | 'dispute-item';

class RequestStatusHelperService {
  static getStatusName(status: RequestStatus | null): string {
    switch (status) {
      case RequestStatus.Submitted:
        return 'item-status.submitted';
      case RequestStatus.InProgress:
        return 'item-status.in-progress';
      case RequestStatus.PendingInformation:
        return 'item-status.pending-information';
      case RequestStatus.Completed:
        return 'item-status.completed';
      case RequestStatus.Cancelled:
        return 'item-status.cancelled';
      case RequestStatus.Initiating:
        return 'request-status.initiating';
      default:
        return 'item-status.unknown';
    }
  }

  static getClassName(status: RequestStatus | null): string {
    switch (status) {
      case RequestStatus.Submitted:
      case RequestStatus.Initiating:
        return 'submitted';
      case RequestStatus.InProgress:
        return 'in-progress';
      case RequestStatus.PendingInformation:
        return 'pending-information';
      case RequestStatus.Completed:
        return 'completed';
      case RequestStatus.Cancelled:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}

class ItemStatusHelperService {
  static getStatusName(status: ItemStatus | null): string {
    switch (status) {
      case ItemStatus.Submitted:
        return 'item-status.submitted';
      case ItemStatus.InProgress:
        return 'item-status.in-progress';
      case ItemStatus.PendingInformation:
        return 'item-status.pending-information';
      case ItemStatus.Completed:
        return 'item-status.completed';
      case ItemStatus.Cancelled:
        return 'item-status.cancelled';
      default:
        return 'item-status.unknown';
    }
  }

  static getClassName(status: ItemStatus | null): string {
    switch (status) {
      case ItemStatus.Submitted:
        return 'submitted';
      case ItemStatus.InProgress:
        return 'in-progress';
      case ItemStatus.PendingInformation:
        return 'pending-information';
      case ItemStatus.Completed:
        return 'completed';
      case ItemStatus.Cancelled:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}

class ItemClassificationCodeStatusHelperService {
  static getStatusName(status: ItemClassificationCodeStatus | null): string {
    return getItemClassificationTypeStatusName(status);
  }

  static getClassName(status: ItemClassificationCodeStatus | null): string {
    return getItemClassificationTypeStatusClassName(status);
  }
}

class InformationRequestStatusHelperService {
  static getStatusName(status: InformationRequestStatus | null): string {
    return getInformationRequestStatusName(status);
  }

  static getClassName(status: InformationRequestStatus | null): string {
    switch (status) {
      case InformationRequestStatus.Open:
        return 'pending-information';
      case InformationRequestStatus.Resolved:
        return 'submitted';
      case InformationRequestStatus.Closed:
        return 'completed';
      default:
        return 'unknown';
    }
  }
}

class DisputeRequestStatusHelperService {
  static getStatusName(status: DisputeRequestStatus | null): string {
    return getDisputeRequestStatusName(status);
  }

  static getClassName(status: DisputeRequestStatus | null): string {
    switch (status) {
      case DisputeRequestStatus.Initiating:
        return 'submitted';
      case DisputeRequestStatus.Submitted:
        return 'submitted';
      case DisputeRequestStatus.InProgress:
        return 'in-progress';
      case DisputeRequestStatus.Completed:
        return 'completed';
      default:
        return 'unknown';
    }
  }
}

class DisputeItemStatusHelperService {
  static getStatusName(status: DisputeRequestItemStatus | null): string {
    return getDisputeRequestItemStatusName(status);
  }

  static getClassName(status: DisputeRequestItemStatus | null): string {
    switch (status) {
      case DisputeRequestItemStatus.Submitted:
        return 'submitted';
      case DisputeRequestItemStatus.InProgress:
        return 'in-progress';
      case DisputeRequestItemStatus.Agreed:
        return 'agreed';
      case DisputeRequestItemStatus.InReclassification:
        return 'in-reclassification';
      case DisputeRequestItemStatus.Disagreed:
        return 'disagreed';
      case DisputeRequestItemStatus.Reclassified:
        return 'reclassified';
      case DisputeRequestItemStatus.Cancelled:
        return 'cancelled';
      default:
        return 'unknown';
    }
  }
}
